import { t, useLocale } from "../lib/i18n";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDialogFocus } from "./useDialogFocus";
import { requestConnectionSettings } from "../lib/api";

const commands = [
  ["new", "Start a new agent", "N"], ["compose", "Focus message composer", ""],
  ["changes", "Inspect changes", ""], ["files", "Browse workspace files", ""],
  ["plan", "Show task plan", ""], ["details", "Runtime settings and details", ""],
  ["compare", "Compare and hand off", ""], ["focus", "Toggle focus mode", ""],
  ["sessions", "Go to sessions", ""], ["activity", "Open activity inbox", ""],
  ["connections", "Connection and appearance settings", ""], ["traces", "Advanced: trace diagnostics", ""], ["jobs", "Advanced: pipeline jobs", ""],
];
export function CommandPalette() {
  useLocale();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [index, setIndex] = useState(0);
  const dialog = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  useDialogFocus(open, dialog, () => setOpen(false));
  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k" && !event.isComposing) {
        if (document.querySelector('[aria-modal="true"]') && !open) return;
        event.preventDefault(); setOpen(value => !value); setSearch(""); setIndex(0);
      }
    };
    window.addEventListener("keydown", keyboard);
    return () => window.removeEventListener("keydown", keyboard);
  }, [open]);
  const filtered = commands.filter(([id, name]) => (location.pathname.startsWith("/sessions") || ["sessions", "activity", "connections", "traces", "jobs"].includes(id)) && (name + " " + t(name)).toLowerCase().includes(search.toLowerCase()));
  function run(id: string) {
    setOpen(false);
    // Wait for modal isolation/focus restoration before focusing a destination.
    requestAnimationFrame(() => {
      if (["sessions", "jobs", "traces"].includes(id)) navigate("/" + id);
      else if (id === "connections") requestConnectionSettings();
      else if (id === "activity") window.dispatchEvent(new Event("swarm-open-activity"));
      else window.dispatchEvent(new CustomEvent("swarm-workbench-command", { detail: id }));
    });
  }
  return <><button type="button" className="command-trigger" aria-label={t("Commands · Ctrl or Command K")} onClick={() => { setOpen(true); setSearch(""); setIndex(0); }}>⌘K</button>
    {open && <div className="dialog-backdrop" onMouseDown={() => setOpen(false)}><section role="dialog" aria-modal="true" aria-labelledby="commands-title" tabIndex={-1} ref={dialog} className="command-dialog" onMouseDown={event => event.stopPropagation()}>
      <h2 id="commands-title">{t("Commands")}</h2>
      <input autoFocus role="combobox" aria-label={t("Find a command")} aria-expanded="true" aria-controls="command-options" aria-activedescendant={filtered[index] ? "command-" + filtered[index][0] : undefined} value={search} onChange={event => { setSearch(event.target.value); setIndex(0); }} onKeyDown={event => {
        if (event.nativeEvent.isComposing) return;
        if (["ArrowDown", "ArrowUp"].includes(event.key)) { event.preventDefault(); setIndex(i => filtered.length ? (i + (event.key === "ArrowDown" ? 1 : filtered.length - 1)) % filtered.length : 0); }
        if (event.key === "Enter" && filtered[index]) { event.preventDefault(); run(filtered[index][0]); }
      }} />
      <div id="command-options" role="listbox" aria-label={t("Available commands")}>{filtered.map(([id, name, shortcut], i) => <button id={"command-" + id} role="option" aria-selected={i === index} type="button" key={id} onMouseMove={() => setIndex(i)} onClick={() => run(id)}>{t(name)}{shortcut && <kbd>{shortcut}</kbd>}</button>)}</div>
      {!filtered.length && <p>{t("No matching commands.")}</p>}
      <p className="panel-note">{t("↑↓ select · Enter open · Esc close. Commands never send prompts or change files.")}</p>
      <button className="text-action" type="button" onClick={() => setOpen(false)}>{t("Close commands")}</button>
    </section></div>}
  </>;
}
